<template>
  <div
    class="behaviour-student-point-history-tab"
    data-testid="behaviour-student-point-history-tab"
  >
    <div v-if="!hidePageHeader" class="d-flex align-items-center gap-2 mb-3">
      <button
        type="button"
        class="btn btn-link p-0 text-primary text-decoration-none d-inline-flex align-items-center behaviour-student-point-history-back"
        data-testid="behaviour-student-point-history-back"
        :aria-label="$t('behaviour_manage.back_to_student_list')"
        @click="onBack"
      >
        <i class="ri-arrow-left-s-line" aria-hidden="true" />
        <span
          class="h5 mb-0 ms-1 text-start behaviour-student-point-history-name"
        >
          {{ studentDisplayName }}
        </span>
      </button>
    </div>

    <div class="row g-3 mb-3 behaviour-point-history-stats">
      <div
        v-for="card in statCards"
        :key="card.key"
        class="col-12 col-sm-6 col-lg-4"
      >
        <BehaviourManageStatCard
          :label="card.label"
          :value="card.value"
          :icon-class="card.iconClass"
          :test-id="'behaviour-student-point-history-stat-' + card.key"
        />
      </div>
    </div>

    <div
      v-if="!hideStaffActions"
      class="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3"
    >
      <BaseButton
        rounded
        type="button"
        data-testid="behaviour-student-point-history-award"
        @click="openAwardModal"
      >
        {{ $t("behaviour_manage.action_award") }}
      </BaseButton>
      <BaseButton
        rounded
        type="button"
        data-testid="behaviour-student-point-history-correction"
        @click="openPointCorrectionModal"
      >
        {{
          $t("behaviour_manage.action_point_correction", {
            point: behaviourTerminology.point
          })
        }}
      </BaseButton>
      <BaseButton
        solid
        rounded
        type="button"
        data-testid="behaviour-student-point-history-redeem"
        @click="openRedeemModal"
      >
        {{ $t("behaviour_manage.action_redeem") }}
      </BaseButton>
    </div>

    <ActionPanel class="mb-3 behaviour-point-history-action-panel">
      <template #left-content>
        <div class="behaviour-point-history-filters w-100 min-w-0">
          <div class="behaviour-point-history-date-range-row mb-3">
            <CustomSelectDatePicker
              v-model="state.filterParams.dates"
              class="behaviour-point-history-date-range-picker w-50"
              :date-ranges="optionalDateRanges"
              :selected-school-year-limit="schoolYearForDatePicker"
              :should-persist-value="true"
              input-id="behaviour-student-point-history-date-range"
              :accessible-label="$t('common.date_range')"
              :placeholder="$t('common.select')"
              data-testid="behaviour-student-point-history-date-range"
              @update:model-value="onDateRangeChange"
            />
          </div>
          <div
            class="d-flex flex-wrap align-items-end gap-3 behaviour-point-history-toolbar"
          >
            <div class="behaviour-point-history-search-field min-w-0">
              <label
                id="behaviour-student-point-history-search-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-student-point-history-search"
              >
                {{ $t("common.search") }}
              </label>
              <InputField
                class="behaviour-point-history-search"
                prepend-icon="ri-search-line"
                :model-value="state.searchInput"
                :placeholder="$t('common.search')"
                input-type="search"
                id="behaviour-student-point-history-search"
                data-testid="behaviour-student-point-history-search"
                @update:model-value="onSearchInput"
              />
            </div>
            <div class="behaviour-point-history-core-value-field min-w-0">
              <label
                id="behaviour-student-point-history-core-value-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-student-point-history-core-value-input"
              >
                {{ behaviourTerminology.coreValue }}
              </label>
              <BehaviourCoreValueLazySelect
                :model-value="state.filterParams.coreValueId"
                class="behaviour-point-history-core-value-filter w-100"
                hide-floating-label
                labelled-by="behaviour-student-point-history-core-value-label"
                :prepend-items="coreValueFilterPrependItems"
                :selected-option="state.coreValueFilterSelectedOption"
                status-filter="all"
                :prefetch-on-mount="false"
                input-id="behaviour-student-point-history-core-value-input"
                append-dropdown-to-body
                append-dropdown-fixed-position
                data-testid="behaviour-student-point-history-core-value-filter"
                @update:model-value="onCoreValueFilterChange"
                @option-selected="onCoreValueFilterOptionSelected"
              />
            </div>
          </div>
        </div>
      </template>
      <template #right-content>
        <div
          class="d-flex align-items-end flex-wrap justify-content-end gap-2 behaviour-point-history-filter-actions"
        >
          <button
            type="button"
            class="btn btn-link d-flex align-items-center p-0 text-primary text-decoration-none"
            :disabled="!hasActiveFilters || state.isLoading"
            data-testid="behaviour-student-point-history-reset-filters"
            @click="resetFilters"
          >
            <i class="ri-filter-off-line" aria-hidden="true" />
            <span class="ms-1 text-decoration-underline">{{
              $t("behaviour_settings.reset_filters")
            }}</span>
          </button>
          <IconButton
            class="h-25"
            :icon="
              state.isExportLoading ? 'ri-loader-4-line' : 'ri-download-2-line'
            "
            :class="{ loading: state.isExportLoading }"
            :disabled="state.isExportLoading || state.isLoading"
            :aria-label="$t('common.download_export')"
            data-testid="behaviour-student-point-history-download"
            @click="downloadStudentPointHistory"
          />
        </div>
      </template>
    </ActionPanel>

    <Loader :is-processing="state.isLoading" />

    <PerPage
      v-if="state.pagination.total > 0"
      :pagination="paginationDisplay"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      class="datatable bg-white text-center mt-3"
      add-table-classes="behaviour-point-history-datatable"
      :fields="tableFields"
      :items="state.tableRows"
      :sorter="{ external: true, resetable: false }"
      :sorter-value="sorterValue"
      :no-items-view="noItemsView"
      data-testid="behaviour-student-point-history-table"
      @update:sorter-value="onSortColumnChange"
    >
      <template #activity="{ item }">
        <td class="text-start">{{ activityLabel(item) }}</td>
      </template>
      <template #coreValueName="{ item }">
        <td class="text-start align-middle">
          <span
            v-if="item.coreValueId || item.coreValueName"
            class="behaviour-core-value-dot me-2"
            :class="indicatorDotClass(item)"
            :style="indicatorDotStyle(item)"
            aria-hidden="true"
          />
          <span
            v-if="item.coreValueId || item.coreValueName"
            class="visually-hidden"
          >
            {{ coreValueIndicatorAriaLabel(item) }}
          </span>
          {{ item.coreValueName }}
        </td>
      </template>
      <template #points="{ item }">
        <td class="text-start behaviour-point-history-points-cell">
          <span
            class="behaviour-point-history-points"
            :class="pointsClass(item)"
          >
            <span class="behaviour-point-history-points__layout">
              <span class="behaviour-point-history-points__value">
                {{ formatPoints(item.points) }}
                <span v-if="isRedeemTransaction(item)" class="visually-hidden">
                  {{ $t("behaviour_manage.transaction_type_redeem") }}
                </span>
              </span>
              <span
                class="behaviour-point-history-points__trailing"
                aria-hidden="true"
              >
                <i
                  v-if="isRedeemTransaction(item)"
                  class="ri-gift-line behaviour-point-history-points__redeem-icon"
                />
              </span>
            </span>
          </span>
        </td>
      </template>
      <template #occurredAt="{ item }">
        <td class="text-start">{{ item.occurredAt }}</td>
      </template>
      <template #awardedBy="{ item }">
        <td class="text-start">{{ item.awardedBy }}</td>
      </template>
      <template #notes="{ item }">
        <td class="text-center behaviour-point-history-notes-cell">
          <BehaviourPointHistoryNotesIcon
            :notes="item.notes"
            :row-id="item.id"
          />
        </td>
      </template>
    </DataTable>

    <div
      v-if="state.isLazyLoadingMode && canLoadMoreLazy"
      ref="lazySentinelRef"
      class="behaviour-core-value-lazy-sentinel py-2 text-center text-muted small"
      data-testid="behaviour-student-point-history-lazy-sentinel"
      aria-hidden="true"
    >
      {{ $t("behaviour_settings.lazy_scroll_hint") }}
    </div>

    <Pagination
      v-if="paginationDisplay.pages > 1 && !state.isLazyLoadingMode"
      :active-page="state.pagination.activePage"
      :pages="paginationDisplay.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    />
  </div>
</template>

<script>
import { useBehaviourTableLazyScroll } from "@/composables/useBehaviourTableLazyScroll.js"
import { useGradeYears } from "@/composables/useGradeYears.js"
import { usePbisPointsSocketRefresh } from "@/composables/usePbisPointsSocketRefresh.js"
import download from "@/helpers/downloadCSV.js"
import {
  formatPointHistoryApiDate,
  isPointHistoryCorrectionTransaction,
  isPointHistoryRedeemTransaction,
  mapPointHistorySortKey
} from "@/helpers/behaviourPointHistoryApi.js"
import {
  indicatorAriaColorFor,
  indicatorDotClassFor,
  indicatorDotStyleFor
} from "@/helpers/behaviourCoreValueIndicator.js"
import {
  buildBehaviourPointHistoryDateRanges,
  getDefaultBehaviourPointHistoryDateRange,
  parseBehaviourPointHistoryDateRange
} from "@/helpers/behaviourPointHistoryFilters.js"
import {
  applyBehaviourTableFetchResult,
  resolveBehaviourTableLazyRequest
} from "@/helpers/behaviourTableLazyLoading.js"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import BehaviourManageStatCard from "@/v3components/Behaviour/BehaviourManageStatCard.vue"
import BehaviourPointHistoryNotesIcon from "@/v3components/Behaviour/BehaviourPointHistoryNotesIcon.vue"
import BehaviourAwardPointsForm from "@/v3components/Forms/Behaviour/BehaviourAwardPointsForm.vue"
import BehaviourPointCorrectionForm from "@/v3components/Forms/Behaviour/BehaviourPointCorrectionForm.vue"
import BehaviourRedeemRewardForm from "@/v3components/Forms/Behaviour/BehaviourRedeemRewardForm.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import CustomSelectDatePicker from "@/v3components/shared/Form/CustomSelectDatePicker.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourStudentPointHistoryTab",
  components: {
    ActionPanel,
    BaseButton,
    IconButton,
    BehaviourCoreValueLazySelect,
    BehaviourManageStatCard,
    BehaviourPointHistoryNotesIcon,
    CustomSelectDatePicker,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  props: {
    student: {
      type: Object,
      required: true,
      validator: (value) => Boolean(value?.studentId ?? value?.id)
    },
    hidePageHeader: {
      type: Boolean,
      default: false
    },
    hideStaffActions: {
      type: Boolean,
      default: false
    }
  },
  emits: ["back"],
  setup(props, { emit }) {
    const SEARCH_DEBOUNCE_MS = 320
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const studentId = computed(
      () => props.student?.studentId ?? props.student?.id
    )

    const studentDisplayName = computed(() => {
      const name = String(props.student?.studentName ?? "").trim()
      if (name) return name
      const num = String(props.student?.studentNumber ?? "").trim()
      if (num) return num
      return t("behaviour_manage.col_student")
    })

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const statCards = computed(() => {
      const bt = behaviourTerminology.value
      const stats = state.stats

      return [
        {
          key: "total-earned",
          label: bt.statTotalPointsEarned,
          value: stats.totalPointsEarned,
          iconClass: "ri-medal-line"
        },
        {
          key: "used-rewards",
          label: bt.statPointsUsedOnRewards,
          value: stats.pointsUsedOnRewards,
          iconClass: "ri-gift-line"
        },
        {
          key: "remaining",
          label: bt.statRemainingPoints,
          value: stats.remainingPoints,
          iconClass: "ri-star-line"
        }
      ]
    })

    const { gradeYears, currentSchoolYear, getMinDate, getMaxDate } =
      useGradeYears()

    const schoolYearForDatePicker = computed(
      () => currentSchoolYear.value || gradeYears.value?.[0] || null
    )

    const coreValueFilterPrependItems = computed(() => [
      { label: t("behaviour_settings.status_all"), value: "all" }
    ])

    let searchDebounceId = null

    const state = reactive({
      isLoading: false,
      isLazyAppendInFlight: false,
      isLazyLoadingMode: false,
      lazyLoadedCount: 0,
      isExportLoading: false,
      searchInput: "",
      tableRows: [],
      stats: {
        totalPointsEarned: 0,
        pointsUsedOnRewards: 0,
        remainingPoints: 0
      },
      filterParams: {
        coreValueId: "all",
        dates: ""
      },
      coreValueFilterSelectedOption: coreValueFilterPrependItems.value[0],
      sortColumn: null,
      sortOrder: null,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      }
    })

    const optionalDateRanges = computed(() =>
      buildBehaviourPointHistoryDateRanges(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const activeDateRange = computed(() =>
      parseBehaviourPointHistoryDateRange(state.filterParams.dates)
    )

    const defaultDateRange = computed(() =>
      getDefaultBehaviourPointHistoryDateRange(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const tableFields = computed(() => {
      const bt = behaviourTerminology.value

      return [
        {
          key: "activity",
          label: t("behaviour_manage.col_activity"),
          sorter: true,
          _style: "width:20%;"
        },
        {
          key: "coreValueName",
          label: bt.coreValue,
          sorter: true,
          _style: "width:18%;"
        },
        {
          key: "points",
          label: bt.points,
          sorter: true,
          _style: "width:10%; min-width:5.75rem;",
          _classes: "behaviour-point-history-points-col"
        },
        {
          key: "occurredAt",
          label: t("behaviour_manage.col_date_time"),
          sorter: true,
          _style: "width:16%;"
        },
        {
          key: "awardedBy",
          label: t("behaviour_manage.col_from"),
          sorter: false,
          _style: "width:16%;"
        },
        {
          key: "notes",
          label: t("behaviour_manage.col_notes"),
          sorter: false,
          _style: "width:8%;"
        }
      ]
    })

    const sorterValue = computed(() => ({
      column: state.sortColumn || "",
      asc: state.sortOrder !== "desc"
    }))

    const coreValueIndicatorAriaLabel = (item) =>
      t("behaviour_settings.indicator_a11y", {
        color: indicatorAriaColorFor(item)
      })

    const paginationDisplay = computed(() => state.pagination)

    const hasActiveFilters = computed(() => {
      const hasSearch = Boolean(String(state.searchInput ?? "").trim())
      const hasCoreValue =
        String(state.filterParams.coreValueId ?? "") !== "all"
      const hasDateRange =
        String(state.filterParams.dates ?? "").trim() !==
        String(defaultDateRange.value ?? "").trim()
      return hasSearch || hasCoreValue || hasDateRange
    })

    const noItemsView = computed(() => ({
      noItems: t("behaviour_manage.no_point_history_rows", {
        point: behaviourTerminology.value.point
      })
    }))

    const formatPoints = (points) => {
      const n = Number(points) || 0
      if (n > 0) return `+${n}`
      return String(n)
    }

    const pointsClass = (item) => {
      const n = Number(item?.points) || 0
      if (n > 0) return "behaviour-point-history-points--positive"
      if (n < 0) return "behaviour-point-history-points--negative"
      return ""
    }

    const isRedeemTransaction = (item) => isPointHistoryRedeemTransaction(item)

    const isCorrectionTransaction = (item) =>
      isPointHistoryCorrectionTransaction(item)

    const activityLabel = (item) => {
      if (isRedeemTransaction(item)) {
        return t("behaviour_manage.activity_reward_redeemed")
      }
      if (isCorrectionTransaction(item)) {
        return t("behaviour_manage.activity_point_correction", {
          point: behaviourTerminology.value.point
        })
      }
      return item?.activity ?? ""
    }

    const buildQuery = () => {
      const range = activeDateRange.value
      return {
        studentId: studentId.value,
        fromDate: formatPointHistoryApiDate(range.start),
        toDate: formatPointHistoryApiDate(range.end),
        page: state.pagination.activePage,
        perPage: state.pagination.per_page?.value || 25,
        search: state.searchInput,
        coreValueId: state.filterParams.coreValueId,
        sortBy: mapPointHistorySortKey(state.sortColumn),
        sortOrder: state.sortOrder
      }
    }

    const fetchStats = async () => {
      const query = buildQuery()
      try {
        const summary = await store.dispatch(
          "behaviourManage/fetchStaffStudentPointsSummary",
          query
        )
        state.stats.totalPointsEarned = summary?.totalPointsEarned ?? 0
        state.stats.pointsUsedOnRewards = summary?.pointsUsedOnRewards ?? 0
        state.stats.remainingPoints = summary?.remainingPoints ?? 0
      } catch {
        state.stats.totalPointsEarned = 0
        state.stats.pointsUsedOnRewards = 0
        state.stats.remainingPoints = 0
      }
    }

    const fetchTransactionsPage = async ({ page, append = false } = {}) => {
      const perPageVal = Number(state.pagination.per_page?.value) || 25
      const perPageLabel =
        state.pagination.per_page?.label || String(perPageVal)
      const { isLazyMode, requestPerPage } =
        resolveBehaviourTableLazyRequest(perPageVal)
      const requestPage = page ?? state.pagination.activePage ?? 1
      const range = activeDateRange.value

      const query = {
        studentId: studentId.value,
        fromDate: formatPointHistoryApiDate(range.start),
        toDate: formatPointHistoryApiDate(range.end),
        page: requestPage,
        perPage: requestPerPage,
        search: state.searchInput,
        coreValueId: state.filterParams.coreValueId,
        sortBy: mapPointHistorySortKey(state.sortColumn),
        sortOrder: state.sortOrder
      }

      if (!query.fromDate || !query.toDate || !query.studentId) {
        return { rows: [], isLazyMode: false }
      }

      const { rows, pagination } = await store.dispatch(
        "behaviourManage/fetchStaffStudentPointTransactions",
        query
      )

      applyBehaviourTableFetchResult({
        state,
        rows: rows || [],
        pagination,
        perPageLabel,
        perPageVal,
        isLazyMode,
        append,
        rowsKey: "tableRows"
      })

      return { rows: rows || [], isLazyMode }
    }

    const fetchTransactions = async ({ append = false } = {}) => {
      try {
        if (append) {
          state.isLazyAppendInFlight = true
        } else {
          state.isLoading = true
        }

        const requestPage = append
          ? (Number(state.pagination.activePage) || 1) + 1
          : Number(state.pagination.activePage) || 1

        const { isLazyMode } = await fetchTransactionsPage({
          page: requestPage,
          append
        })

        if (append && isLazyMode) {
          state.pagination.activePage = requestPage
        }
      } catch {
        if (!append) {
          state.tableRows = []
          state.pagination = {
            ...state.pagination,
            total: 0,
            pages: 0,
            from: 0,
            to: 0
          }
        }
      } finally {
        state.isLoading = false
        state.isLazyAppendInFlight = false
        await nextTick()
        setupLazyObserver()
      }
    }

    const {
      lazySentinelRef,
      canLoadMoreLazy,
      setupLazyObserver,
      teardownLazyObserver
    } = useBehaviourTableLazyScroll({
      state,
      getVisibleCount: () => state.tableRows.length,
      onAppend: () => fetchTransactions({ append: true })
    })

    const downloadStudentPointHistory = async () => {
      const query = buildQuery()
      if (
        !query.fromDate ||
        !query.toDate ||
        !query.studentId ||
        state.isExportLoading
      ) {
        return
      }

      const exportFileName =
        String(studentDisplayName.value ?? "")
          .trim()
          .replace(/\s+/g, "-")
          .toLowerCase() || "student-point-history"

      try {
        state.isExportLoading = true
        const exportResult = await store.dispatch(
          "behaviourManage/exportStaffStudentPointTransactions",
          query
        )
        if (exportResult?.url) {
          await download.downloadFromUrl(
            exportResult.url,
            exportResult.fileName || exportFileName
          )
        } else if (exportResult?.csvContent) {
          download.CSVExport(exportResult.csvContent, exportFileName)
        }
      } catch {
        // Export errors are surfaced by the global axios handler when applicable.
      } finally {
        state.isExportLoading = false
      }
    }

    const refreshData = async () => {
      await Promise.all([fetchTransactions(), fetchStats()])
    }

    usePbisPointsSocketRefresh({
      refresh: refreshData,
      studentId
    })

    const onSearchInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        fetchTransactions()
      }, SEARCH_DEBOUNCE_MS)
    }

    const onCoreValueFilterChange = (value) => {
      state.filterParams.coreValueId = value ?? "all"
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const onCoreValueFilterOptionSelected = (option) => {
      state.coreValueFilterSelectedOption = option ? { ...option } : null
    }

    const onDateRangeChange = () => {
      state.pagination.activePage = 1
      refreshData()
    }

    const initializeFilters = () => {
      state.filterParams.dates = defaultDateRange.value
      state.filterParams.coreValueId = "all"
      state.coreValueFilterSelectedOption = coreValueFilterPrependItems.value[0]
    }

    const resetFilters = () => {
      if (state.isLoading) return
      state.searchInput = ""
      state.sortColumn = null
      state.sortOrder = null
      initializeFilters()
      state.pagination.activePage = 1
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
      refreshData()
    }

    const onSortColumnChange = (sorterState) => {
      const column = String(sorterState?.column ?? "").trim()
      if (!column) {
        state.sortColumn = null
        state.sortOrder = null
      } else if (mapPointHistorySortKey(column)) {
        state.sortColumn = column
        state.sortOrder = sorterState?.asc ? "asc" : "desc"
      } else {
        return
      }
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      fetchTransactions()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      fetchTransactions()
    }

    const onBack = () => {
      emit("back")
    }

    const openRedeemModal = () => {
      modal?.open?.(BehaviourRedeemRewardForm, {
        size: "lg",
        title: t("behaviour_manage.redeem_reward_title"),
        props: {}
      })
    }

    const openPointCorrectionModal = () => {
      modal?.open?.(BehaviourPointCorrectionForm, {
        size: "lg",
        title: t("behaviour_manage.point_correction_title", {
          point: behaviourTerminology.value.point
        }),
        props: {}
      })
    }

    const openAwardModal = () => {
      modal?.open?.(BehaviourAwardPointsForm, {
        size: "lg",
        title: t("behaviour_manage.award_points_title", {
          points: behaviourTerminology.value.points
        }),
        props: {}
      })
    }

    watch(
      () => modal?.options?.action,
      (action) => {
        if (props.hideStaffActions) return
        if (
          action === "pointHistoryRedeemed" ||
          action === "pointHistoryCorrected" ||
          action === "pointHistoryAwarded"
        ) {
          if (modal?.options) {
            modal.options.action = ""
            modal.options.actionData = null
          }
          state.pagination.activePage = 1
          refreshData()
        }
      }
    )

    watch(studentId, () => {
      state.pagination.activePage = 1
      initializeFilters()
      refreshData()
    })

    onMounted(async () => {
      initializeFilters()
      await refreshData()
    })

    onUnmounted(() => {
      teardownLazyObserver()
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
    })

    return {
      behaviourTerminology,
      studentDisplayName,
      statCards,
      schoolYearForDatePicker,
      coreValueFilterPrependItems,
      optionalDateRanges,
      state,
      tableFields,
      paginationDisplay,
      hasActiveFilters,
      noItemsView,
      formatPoints,
      pointsClass,
      isRedeemTransaction,
      activityLabel,
      indicatorDotClass: indicatorDotClassFor,
      indicatorDotStyle: indicatorDotStyleFor,
      coreValueIndicatorAriaLabel,
      onSearchInput,
      onCoreValueFilterChange,
      onCoreValueFilterOptionSelected,
      onDateRangeChange,
      resetFilters,
      onSortColumnChange,
      sorterValue,
      downloadStudentPointHistory,
      lazySentinelRef,
      canLoadMoreLazy,
      setPerPage,
      setActivePage,
      onBack,
      openRedeemModal,
      openPointCorrectionModal,
      openAwardModal
    }
  }
}
</script>
